import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, FileText, Download, FileCheck, Sparkles, Type, Image, Loader2, Mail, Send, Eye, BookOpen, Trash2, Clock, Share2, Twitter, Facebook, Link2, RefreshCw, Wand2, Upload, Camera, BookMarked, CheckCircle2, AlertCircle, Circle } from 'lucide-react';
import { BOOK_THEMES, generatePDFBase64, generateWordDocBase64, generateEPubBase64, generateProfessionalPDFBase64 } from '@/utils/bookExport';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVER_MOODS = [
  { id: 'dramatic', name: 'Dramatic', emoji: '🎭', description: 'Bold & cinematic' },
  { id: 'warm', name: 'Warm', emoji: '☀️', description: 'Cozy & inviting' },
  { id: 'mysterious', name: 'Mysterious', emoji: '🌙', description: 'Dark & intriguing' },
  { id: 'playful', name: 'Playful', emoji: '🎨', description: 'Fun & colorful' },
  { id: 'professional', name: 'Professional', emoji: '💼', description: 'Clean & elegant' },
];

const packageToSuggestions = (pkg) => {
  if (!pkg) return null;
  const assets = pkg.assets || {};
  const readiness = pkg.readiness || {};
  const subtitle = assets.subtitle_options?.[0];
  const cover = assets.cover_brief || {};

  return {
    export_ready_score: readiness.score,
    positioning: pkg.positioning || {},
    readiness_check: readiness,
    book_title_alternatives: assets.title_options || [],
    subtitle_suggestion: subtitle ? {
      subtitle: subtitle.subtitle,
      works_with: subtitle.why,
    } : null,
    chapter_improvements: [],
    back_cover_blurb: assets.back_cover_copy || assets.book_description,
    marketing_description: assets.book_description,
    author_bio: assets.author_bio,
    keywords: assets.keywords || [],
    categories: assets.category_suggestions || [],
    cover_image_prompt: {
      prompt: cover.prompt || '',
      mood: cover.mood || 'warm',
      style: cover.style || 'refined editorial',
      color_palette: cover.color_palette || [],
    },
    recommendations: [
      ...(readiness.needs_attention || []),
      ...(readiness.optional_improvements || []),
      ...(readiness.next_best_action ? [readiness.next_best_action] : []),
    ],
    publishing_package: pkg,
  };
};

function ExportBookModal({ book, chapters, onClose }) {
  const navigate = useNavigate();
  const [authorName, setAuthorName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('ai'); // Default to AI-designed
  const [exporting, setExporting] = useState(false);
  const [exports, setExports] = useState([]);
  const [loadingExports, setLoadingExports] = useState(true);
  const [activeTab, setActiveTab] = useState('export');

  // Book Review state
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  
  // Ref for custom image file input
  const imageInputRef = useRef(null);
  
  // Export success state - shows preview after export
  const [exportSuccess, setExportSuccess] = useState(null); // { format, fileName, fileContent, fileSize }
  
  // Email export state
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Cover options state
  const [coverType, setCoverType] = useState('classic');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [coverMood, setCoverMood] = useState('dramatic');
  const [coverCustomPrompt, setCoverCustomPrompt] = useState('');
  const [generatedCover, setGeneratedCover] = useState(book.cover_image_base64 || null);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  // AI Format Guide state
  const [formatGuide, setFormatGuide] = useState(
    (book.format_guide && Object.keys(book.format_guide).length > 0) ? book.format_guide : null
  );
  const [generatingFormat, setGeneratingFormat] = useState(false);

  // Publishing assistant state
  const [, setPublishingPackage] = useState(
    (book.publishing_package && Object.keys(book.publishing_package).length > 0)
      ? book.publishing_package
      : null
  );
  const [exportSuggestions, setExportSuggestions] = useState(
    (book.export_suggestions && Object.keys(book.export_suggestions).length > 0)
      ? book.export_suggestions
      : (book.publishing_package && Object.keys(book.publishing_package).length > 0)
        ? packageToSuggestions(book.publishing_package)
        : null
  );
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  
  // Download state for history items
  const [downloading, setDownloading] = useState(null);
  
  // Custom image upload state
  const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
  const [uploadedImageBase64, setUploadedImageBase64] = useState(null);

  const loadExports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books/${book.book_id}/exports`);
      setExports(response.data);
    } catch (error) {
      console.error('Failed to load exports:', error);
    } finally {
      setLoadingExports(false);
    }
  }, [book.book_id]);

  useEffect(() => {
    loadExports();
    if (book.cover_image_base64) {
      setGeneratedCover(book.cover_image_base64);
      setCoverType('ai');
    }
    if (book.format_guide && Object.keys(book.format_guide).length > 0) {
      setFormatGuide(book.format_guide);
    }
    if (book.publishing_package && Object.keys(book.publishing_package).length > 0) {
      setPublishingPackage(book.publishing_package);
      const suggs = (book.export_suggestions && Object.keys(book.export_suggestions).length > 0)
        ? book.export_suggestions
        : packageToSuggestions(book.publishing_package);
      setExportSuggestions(suggs);
    } else if (book.export_suggestions && Object.keys(book.export_suggestions).length > 0) {
      setExportSuggestions(book.export_suggestions);
    }
  }, [book.book_id, book.cover_image_base64, book.format_guide, book.export_suggestions, book.publishing_package, loadExports]);

  const handleGenerateCover = async () => {
    setGeneratingCover(true);
    try {
      const response = await axios.post(
        `${API}/books/${book.book_id}/generate-cover`,
        { 
          mood: coverMood,
          custom_prompt: coverCustomPrompt.trim() || null
        },
        {timeout: 120000 } // 2 min timeout for image generation
      );
      
      setGeneratedCover(response.data.image_base64);
      toast.success('Cover generated! ✨');
    } catch (error) {
      console.error('Cover generation failed:', error);
      const msg = error.response?.data?.detail || 'Failed to generate cover. Please try again.';
      toast.error(msg);
    } finally {
      setGeneratingCover(false);
    }
  };

  // Handle custom image upload from device
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Please select an image under 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      // Extract base64 data (remove the data:image/xxx;base64, prefix)
      const base64Data = dataUrl.split(',')[1];
      
      setUploadedImagePreview(dataUrl);
      setUploadedImageBase64(base64Data);
      toast.success('Image uploaded! It will be used as your cover.');
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  // Trigger file input click
  const triggerImageUpload = () => {
    imageInputRef.current?.click();
  };

  // Generate AI format guide for professional export
  const handleGenerateFormatGuide = async () => {
    setGeneratingFormat(true);
    try {
      const response = await axios.post(
        `${API}/books/${book.book_id}/generate-format-guide`,
        {},
        {timeout: 60000 }
      );
      
      setFormatGuide(response.data);
      toast.success('Professional format guide created! ✨');
    } catch (error) {
      console.error('Format guide generation failed:', error);
      toast.error('Failed to generate format guide. Using default settings.');
    } finally {
      setGeneratingFormat(false);
    }
  };

  // Generate publishing package
  const handleGenerateExportSuggestions = async () => {
    setGeneratingSuggestions(true);
    try {
      const response = await axios.post(
        `${API}/books/${book.book_id}/publishing/analyze`,
        {},
        {timeout: 90000 }
      );
      
      setPublishingPackage(response.data);
      setExportSuggestions(packageToSuggestions(response.data));
      if (response.data?.format_guide) {
        setFormatGuide(response.data.format_guide);
      }
      toast.success('Publishing package created!');
    } catch (error) {
      console.error('Publishing assistant failed:', error);
      toast.error('Could not build the publishing package. Please try again.');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // Use AI-suggested cover prompt
  const handleUseSuggestedCoverPrompt = () => {
    if (exportSuggestions?.cover_image_prompt?.prompt) {
      setCoverCustomPrompt(exportSuggestions.cover_image_prompt.prompt);
      setCoverMood(exportSuggestions.cover_image_prompt.mood || 'dramatic');
      toast.success('Cover prompt applied! Click "Generate Cover" to create it.');
    }
  };

  const getCoverImageForExport = () => {
    switch (coverType) {
      case 'ai':
        return generatedCover ? `data:image/png;base64,${generatedCover}` : null;
      case 'image':
        // Prioritize uploaded image, fall back to URL
        if (uploadedImageBase64) {
          return `data:image/png;base64,${uploadedImageBase64}`;
        }
        return coverImageUrl || null;
      case 'classic':
      default:
        return null;
    }
  };

  const handleRunReview = async () => {
    setReviewLoading(true);
    try {
      const response = await axios.post(`${API}/books/${book.book_id}/review`, {}, { timeout: 60000 });
      setReview(response.data);
    } catch (error) {
      console.error('Review failed:', error);
      toast.error('Could not run review right now. Try again in a moment.');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleGoToChapter = (chapterNumber) => {
    const ch = chapters.find(c => c.chapter_number === chapterNumber);
    if (ch) {
      onClose();
      navigate(`/chapters/${ch.chapter_id}`);
    }
  };

  const handleExport = async (format) => {
    if (!authorName.trim()) {
      toast.error('Please enter author name');
      return;
    }

    if (chapters.length === 0) {
      toast.error('No chapters to export');
      return;
    }

    setExporting(true);
    try {
      let fileName;
      let fileSize = 0;
      let fileContent = null;
      const coverImage = getCoverImageForExport();
      
      switch (format) {
        case 'pdf':
          // Use AI-enhanced professional PDF if AI theme selected
          if (selectedTheme === 'ai') {
            // Generate format guide if not already generated
            let guide = formatGuide;
            if (!guide) {
              toast.info('Creating professional design...');
              try {
                const response = await axios.post(
                  `${API}/books/${book.book_id}/generate-format-guide`,
                  {},
                  {timeout: 60000 }
                );
                guide = response.data;
                setFormatGuide(guide);
              } catch (err) {
                // Using default format guide
              }
            }
            const pdfResult = await generateProfessionalPDFBase64(book, chapters, authorName, coverImage, guide);
            fileName = pdfResult.fileName;
            fileContent = pdfResult.base64;
          } else {
            const pdfResult = await generatePDFBase64(book, chapters, selectedTheme, authorName, coverImage);
            fileName = pdfResult.fileName;
            fileContent = pdfResult.base64;
          }
          fileSize = Math.round(fileContent.length * 0.75);
          break;
        case 'word': {
          const wordResult = await generateWordDocBase64(book, chapters, selectedTheme, authorName, coverImage);
          fileName = wordResult.fileName;
          fileContent = wordResult.base64;
          fileSize = Math.round(fileContent.length * 0.75);
          break;
        }
        case 'epub': {
          const epubResult = await generateEPubBase64(book, chapters, authorName, coverImage);
          fileName = epubResult.fileName;
          fileContent = epubResult.base64;
          fileSize = Math.round(fileContent.length * 0.75);
          break;
        }
        case 'email':
          setShowEmailForm(true);
          setExporting(false);
          return;
        default:
          throw new Error('Unknown format');
      }
      
      // Save export record (metadata only — file_content excluded to stay under MongoDB's 16 MB doc limit)
      try {
        await axios.post(`${API}/books/${book.book_id}/exports`, {
          file_name: fileName,
          format: format,
          theme: selectedTheme,
          author_name: authorName,
          file_size: fileSize,
        });

        await loadExports();
      } catch (saveError) {
        console.error('Failed to save export record:', saveError);
        toast.warning('Export downloaded but history could not be saved.');
      }
      
      // Set success state to show preview
      setExportSuccess({
        format: format,
        fileName: fileName,
        fileContent: fileContent,
        fileSize: fileSize
      });
      
      toast.success(`${format.toUpperCase()} created successfully!`);
      
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export book: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Download the current export success file
  const handleDownloadSuccess = () => {
    if (!exportSuccess) return;
    
    const { format, fileName, fileContent } = exportSuccess;
    
    const byteCharacters = atob(fileContent);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    
    let mimeType = 'application/octet-stream';
    if (format === 'pdf') mimeType = 'application/pdf';
    else if (format === 'word') mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    else if (format === 'epub') mimeType = 'application/epub+zip';
    
    const blob = new Blob([byteArray], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('File downloaded!');
  };

  // Open PDF in new tab for preview
  const handlePreviewPDF = () => {
    if (!exportSuccess || exportSuccess.format !== 'pdf') return;
    
    const byteCharacters = atob(exportSuccess.fileContent);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  // Share functionality
  const handleShare = async (platform) => {
    const shareText = `Check out my book "${book.title}" by ${authorName || 'Anonymous'}!`;
    const shareUrl = window.location.href;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          toast.success('Link copied to clipboard!');
        } catch (err) {
          toast.error('Failed to copy link');
        }
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(`My Book: ${book.title}`)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
        break;
      default:
        break;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  /**
   * Rebuild a previously exported format.
   *
   * Export records deliberately store metadata only — the file bytes are not
   * saved, because a full book in base64 exceeds MongoDB's 16MB document limit.
   * So GET /exports/{id}/download could only ever 404, and every row in this
   * history was a button that failed. Regenerating from the current book is
   * both possible and more useful: the reader gets today's text, including any
   * chapters they have polished since.
   */
  const rebuildSavedExport = async (exportRecord) => {
    const theme = exportRecord.theme || selectedTheme;
    const author = exportRecord.author_name || authorName;
    const coverImage = getCoverImageForExport();
    const usesProfessionalFormat = theme === 'ai';
    switch (exportRecord.format) {
      case 'word':
        return { ...(await generateWordDocBase64(book, chapters, theme, author)), format: 'word' };
      case 'epub':
        return { ...(await generateEPubBase64(book, chapters, author, coverImage)), format: 'epub' };
      case 'pdf':
      default:
        return {
          ...(usesProfessionalFormat
            ? await generateProfessionalPDFBase64(book, chapters, author, coverImage, formatGuide)
            : await generatePDFBase64(book, chapters, theme, author, coverImage)),
          format: 'pdf',
        };
    }
  };

  const triggerDownload = (base64, fileName, format) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const mimeType = format === 'pdf'
      ? 'application/pdf'
      : format === 'word'
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : format === 'epub'
      ? 'application/epub+zip'
      : 'application/octet-stream';
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Download a saved export from history
  const handleDownloadSaved = async (exportRecord) => {
    setDownloading(exportRecord.export_id);
    try {
      const rebuilt = await rebuildSavedExport(exportRecord);
      triggerDownload(rebuilt.base64, exportRecord.file_name || rebuilt.fileName, rebuilt.format);
      toast.success('Downloaded.');
    } catch (error) {
      console.error('Failed to rebuild export:', error);
      toast.error('Could not rebuild that file. Try exporting again.');
    } finally {
      setDownloading(null);
    }
  };

    // View a saved export (opens in new tab for PDF)
  // View a saved export. Rebuilds for the same reason as the download above —
  // the stored record has never contained the file itself.
  const handleViewExport = async (exportRecord) => {
    if (exportRecord.format !== 'pdf') {
      toast.info('Opening in the browser only works for PDFs. Use Download instead.');
      return;
    }
    setDownloading(exportRecord.export_id);
    try {
      const rebuilt = await rebuildSavedExport({ ...exportRecord, format: 'pdf' });
      const byteCharacters = atob(rebuilt.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Give the new tab time to take the URL before revoking it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Failed to rebuild export for viewing:', error);
      toast.error('Could not open that file. Try exporting again.');
    } finally {
      setDownloading(null);
    }
  };

  // Send book via email
  const handleEmailExport = async () => {
    if (!emailRecipient.trim()) {
      toast.error('Please enter recipient email');
      return;
    }

    setExporting(true);
    try {
      // Create email with book content
      const bookContent = chapters.map(c => 
        `Chapter ${c.chapter_number}: ${c.title}\n\n${c.polished_content || c.content || ''}`
      ).join('\n\n---\n\n');
      
      const subject = encodeURIComponent(`${book.title} by ${authorName || 'Author'}`);
      const body = encodeURIComponent(`Here is the book "${book.title}":\n\n${bookContent}\n\n---\nCreated with The Storyteller`);
      
      // Open email client with pre-filled content
      window.location.href = `mailto:${emailRecipient}?subject=${subject}&body=${body}`;
      
      // Save export record
      await axios.post(`${API}/books/${book.book_id}/exports`, {
        file_name: `${book.title} - Email to ${emailRecipient}`,
        format: 'email',
        theme: selectedTheme,
        author_name: authorName,
        file_size: bookContent.length
      });
      
      await loadExports();
      setShowEmailForm(false);
      setEmailRecipient('');
      toast.success('Email client opened!');
      
    } catch (error) {
      console.error('Email export failed:', error);
      toast.error('Failed to prepare email');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteExport = async (exportId) => {
    try {
      await axios.delete(`${API}/exports/${exportId}`);
      setExports(exports.filter(e => e.export_id !== exportId));
      toast.success('Export record deleted');
    } catch (error) {
      console.error('Failed to delete export:', error);
      toast.error('Failed to delete export');
    }
  };

  // Share cover to social media
  const handleShareCover = async (platform) => {
    if (!generatedCover) {
      toast.error('Generate a cover first to share');
      return;
    }

    const shareText = `Check out my book "${book.title}" - created with The Storyteller!`;
    const shareUrl = window.location.origin;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank'
        );
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
          '_blank'
        );
        break;
      case 'copy':
        // Download cover as image for manual sharing
        try {
          const link = document.createElement('a');
          link.href = `data:image/png;base64,${generatedCover}`;
          link.download = `${book.title.replace(/[^a-z0-9]/gi, '_')}_cover.png`;
          link.click();
          toast.success('Cover image downloaded! Share it anywhere.');
        } catch (error) {
          toast.error('Failed to download cover');
        }
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'pdf': return '📕';
      case 'word': return '📘';
      case 'email': return '📧';
      default: return '📄';
    }
  };

  const theme = BOOK_THEMES[selectedTheme];
  const tabClass = (tab) =>
    `story-tab flex-1 justify-center rounded-none border-b-2 border-transparent px-2 sm:px-4 ${
      activeTab === tab ? 'story-tab-active !border-[var(--story-primary)]' : ''
    }`;

  return (
    <div className="fixed inset-0 bg-black/45 dark:bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-[var(--story-bg)] border border-[var(--story-line)] rounded-t-[1.5rem] sm:rounded-[1.25rem] shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--story-bg)] border-b border-[var(--story-line)] px-4 sm:px-6 py-3 flex items-center justify-between z-10">
          <h2 className="story-section-title text-lg sm:text-xl flex items-center">
            <Download className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            Export Book
          </h2>
          <button onClick={onClose} className="text-[#2A2420] dark:text-[#F0EBE4] hover:text-[#2A2420] dark:hover:text-white p-2">
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Export Success View */}
        {exportSuccess && (
          <div className="p-6 sm:p-8 text-center">
            {/* Success Animation */}
            <div className="mb-6">
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
                exportSuccess.format === 'pdf' ? 'bg-red-100 dark:bg-red-900/30' :
                exportSuccess.format === 'word' ? 'bg-blue-100 dark:bg-blue-900/30' :
                'bg-green-100 dark:bg-green-900/30'
              }`}>
                <FileCheck className={`w-10 h-10 ${
                  exportSuccess.format === 'pdf' ? 'text-red-600 dark:text-red-400' :
                  exportSuccess.format === 'word' ? 'text-blue-600 dark:text-blue-400' :
                  'text-green-600 dark:text-green-400'
                }`} />
              </div>
            </div>
            
            <h3 className="text-2xl font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2">
              Your Book is Ready! 🎉
            </h3>
            <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-6">
              {exportSuccess.fileName}
              <span className="mx-2">•</span>
              {formatFileSize(exportSuccess.fileSize)}
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8">
              <Button
                onClick={handleDownloadSuccess}
                className="flex-1 bg-[#2A2420] hover:bg-[#1A1A1A] dark:bg-[#F8F5F0] dark:hover:bg-white dark:text-[#2A2420] text-white min-h-[48px]"
              >
                <Download className="w-5 h-5 mr-2" />
                Download
              </Button>
              
              {exportSuccess.format === 'pdf' && (
                <Button
                  onClick={handlePreviewPDF}
                  variant="outline"
                  className="flex-1 border-[#8B7355] text-[#7C766E] hover:bg-[#8B7355]/10 min-h-[48px]"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Preview PDF
                </Button>
              )}
            </div>
            
            {/* Share Section */}
            <div className="border-t border-[#E0D8CC] dark:border-[#2A2520]/50 pt-6">
              <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-4">Share your achievement</p>
              <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto">
                <button
                  onClick={() => handleShare('twitter')}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 text-[#1DA1F2] transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                  <span className="text-sm font-medium">Twitter</span>
                </button>
                <button
                  onClick={() => handleShare('facebook')}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#4267B2]/10 hover:bg-[#4267B2]/20 text-[#4267B2] transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="text-sm font-medium">Facebook</span>
                </button>
                <button
                  onClick={() => handleShare('email')}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-[#2A2520] dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-sm font-medium">Email</span>
                </button>
                <button
                  onClick={() => handleShare('copy')}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#8B7355]/10 hover:bg-[#8B7355]/20 text-[#7C766E] transition-colors"
                >
                  <Link2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Copy Link</span>
                </button>
              </div>
            </div>
            
            {/* Create Another */}
            <div className="mt-8 flex justify-center gap-3">
              <Button
                onClick={() => setExportSuccess(null)}
                variant="ghost"
                className="text-[#7C766E]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Create Another Export
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-[#2A2420]/60 dark:text-[#F0EBE4]/60"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Normal Export View */}
        {!exportSuccess && (
          <>
        {/* Tabs */}
        <div className="flex border-b border-[var(--story-line)] overflow-x-auto">
          <button
            onClick={() => setActiveTab('export')}
            data-testid="export-tab"
            className={tabClass('export')}
          >
            <Download className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">New </span>Export
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            data-testid="suggestions-tab"
            className={tabClass('suggestions')}
          >
            <Sparkles className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Publishing </span>Assistant
          </button>
          <button
            onClick={() => setActiveTab('review')}
            data-testid="review-tab"
            className={tabClass('review')}
          >
            <BookMarked className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Editorial </span>Review
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="history-tab"
            className={tabClass('history')}
          >
            <FileCheck className="w-4 h-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">My </span>Exports ({exports.length})
          </button>
        </div>

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Author Name */}
            <div>
              <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                Author Name *
              </label>
              <Input
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                placeholder="Enter your name"
                data-testid="author-name-input"
                className="w-full dark:bg-[#2A2520] dark:text-[#F0EBE4] dark:border-[#2A2520]"
                required
              />
            </div>

            {/* Cover Page Selection */}
            <div>
              <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2 sm:mb-3">
                Choose Your Cover Style
              </label>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {/* Classic Option */}
                <button
                  type="button"
                  onClick={() => setCoverType('classic')}
                  data-testid="cover-type-classic"
                  className={`p-2 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all text-center ${
                    coverType === 'classic'
                      ? 'border-[#2D2D2D] dark:border-[#F5F1E8] bg-[#F8F5F0] dark:bg-[#2A2520]'
                      : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#D4C4A8]'
                  }`}
                >
                  <Type className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 text-[#2A2420] dark:text-[#F0EBE4]" />
                  <div className="font-semibold text-xs sm:text-base text-[#2A2420] dark:text-[#F0EBE4]">Classic</div>
                  <div className="hidden sm:block text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">Elegant typography</div>
                </button>

                {/* Your Image Option */}
                <button
                  type="button"
                  onClick={() => setCoverType('image')}
                  data-testid="cover-type-image"
                  className={`p-2 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all text-center ${
                    coverType === 'image'
                      ? 'border-[#2D2D2D] dark:border-[#F5F1E8] bg-[#F8F5F0] dark:bg-[#2A2520]'
                      : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#D4C4A8]'
                  }`}
                >
                  <Image className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 text-[#2A2420] dark:text-[#F0EBE4]" />
                  <div className="font-semibold text-xs sm:text-base text-[#2A2420] dark:text-[#F0EBE4]">Image</div>
                  <div className="hidden sm:block text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">Add your own</div>
                </button>

                {/* AI Magic Option */}
                <button
                  type="button"
                  onClick={() => setCoverType('ai')}
                  data-testid="cover-type-ai"
                  className={`p-2 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all text-center ${
                    coverType === 'ai'
                      ? 'border-[#8B7A97] bg-[#F2EEF5] dark:bg-[#2B2530]'
                      : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#B9A9C4]'
                  }`}
                >
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 text-[#6B5A7B] dark:text-[#C9BBD8]" />
                  <div className="font-semibold text-xs sm:text-base text-[#2A2420] dark:text-[#F0EBE4]">AI Magic</div>
                  <div className="hidden sm:block text-xs text-[#6B5A7B] dark:text-[#C9BBD8]">We create it</div>
                </button>
              </div>
            </div>

            {/* Image URL Input (shown when 'image' is selected) */}
            {coverType === 'image' && (
              <div className="bg-[#F8F5F0] dark:bg-[#2A2520]/50 rounded-xl p-4 space-y-4">
                {/* Hidden file input */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="cover-image-file-input"
                />
                
                {/* Upload from Device - Primary Option */}
                <div>
                  <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                    Upload from Your Device
                  </label>
                  <Button
                    type="button"
                    onClick={triggerImageUpload}
                    data-testid="upload-image-button"
                    className="w-full bg-[#8B7355] hover:bg-[#6B5344] text-white py-6 flex flex-col items-center"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <Upload className="w-5 h-5" />
                      <Camera className="w-5 h-5" />
                    </div>
                    <span className="font-bold">Choose Photo or Take Picture</span>
                    <span className="text-xs opacity-80">Camera, Photo Library, or Files</span>
                  </Button>
                </div>

                {/* Show uploaded image preview */}
                {uploadedImagePreview && (
                  <div className="mt-3">
                    <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-2">Your uploaded cover:</p>
                    <div className="relative inline-block">
                      <img 
                        src={uploadedImagePreview}
                        alt="Uploaded cover preview"
                        className="max-h-48 rounded-lg shadow-md mx-auto"
                      />
                      <button
                        onClick={() => {
                          setUploadedImagePreview(null);
                          setUploadedImageBase64(null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Alternative: URL Input */}
                {!uploadedImagePreview && (
                  <div className="pt-3 border-t border-[#E0D8CC] dark:border-[#2A2520]">
                    <p className="text-xs text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mb-2 text-center">— or paste an image URL —</p>
                    <Input
                      value={coverImageUrl}
                      onChange={(e) => setCoverImageUrl(e.target.value)}
                      placeholder="https://example.com/your-cover-image.jpg"
                      data-testid="cover-image-url-input"
                      className="w-full dark:bg-[#2A2520] dark:text-[#F0EBE4] dark:border-[#2A2520]"
                    />
                  </div>
                )}
                
                <p className="text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                  Works best with vertical/portrait images. Supported: JPG, PNG, WebP.
                </p>
              </div>
            )}

            {/* AI Mood Selection (shown when 'ai' is selected) */}
            {coverType === 'ai' && (
              <div className="bg-[#F4F0EC] dark:bg-[#2A2520]/70 rounded-xl p-4 space-y-4">
                <label className="block text-sm font-medium text-[#3D3530] dark:text-[#F0EBE4]">
                  What vibe should the cover have?
                </label>
                <div className="flex flex-wrap gap-2">
                  {COVER_MOODS.map((mood) => (
                    <button
                      key={mood.id}
                      type="button"
                      onClick={() => setCoverMood(mood.id)}
                      data-testid={`mood-${mood.id}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        coverMood === mood.id
                          ? 'bg-[#5C4D6B] text-white'
                          : 'bg-white dark:bg-[#2A2520] text-[#2A2420] dark:text-[#F0EBE4] hover:bg-[#E8E0D4] dark:hover:bg-[#342C26]'
                      }`}
                    >
                      {mood.emoji} {mood.name}
                    </button>
                  ))}
                </div>

                {/* Optional custom description - PROMINENT */}
                <div className="bg-white dark:bg-[#2A2520] rounded-xl p-4 border border-[#D8CBB8] dark:border-[#3A3028]">
                  <label className="block text-sm font-bold text-[#3D3530] dark:text-[#F0EBE4] mb-2">
                    Describe Your Cover Vision (optional)
                  </label>
                  <textarea
                    value={coverCustomPrompt}
                    onChange={(e) => setCoverCustomPrompt(e.target.value)}
                    placeholder="Example: A dramatic scene on a mountain with fire blowing from a volcano, dark stormy sky..."
                    data-testid="cover-custom-prompt"
                    rows={3}
                    spellCheck={true}
                    className="w-full px-3 py-2 border border-[#D8CBB8] dark:border-[#3A3028] rounded-lg bg-[#FBF8F2] dark:bg-[#22201C] text-[#2A2420] dark:text-[#F0EBE4] focus:outline-none focus:ring-2 focus:ring-[#8B7A97] placeholder:text-[#9C9084]"
                  />
                  <p className="text-xs text-[#6B5A7B] dark:text-[#C9BBD8] mt-2">
                    Be specific! Describe scenes, colors, mood. Leave blank to let AI decide based on your book's title.
                  </p>
                </div>
                
                <Button
                  onClick={handleGenerateCover}
                  disabled={generatingCover}
                  data-testid="generate-cover-button"
                  className="w-full bg-[#5C4D6B] hover:bg-[#4A3D58] text-white"
                >
                  {generatingCover ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating... (this may take a minute)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatedCover ? 'Regenerate Cover' : 'Generate Cover'}
                    </>
                  )}
                </Button>

                {/* Show generated cover preview with share options */}
                {generatedCover && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-[#6B5A7B] dark:text-[#C9BBD8]">Generated Cover:</p>
                      <div className="relative">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowShareMenu(!showShareMenu)}
                          data-testid="share-cover-button"
                          className="text-[#5C4D6B] border-[#CDBED8] hover:bg-[#F2EEF5]"
                        >
                          <Share2 className="w-4 h-4 mr-1" />
                          Share
                        </Button>
                        {showShareMenu && (
                          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-[#22201C] rounded-lg shadow-lg border border-gray-200 dark:border-[#2A2520]/50 py-2 z-20 min-w-[160px]">
                            <button
                              onClick={() => handleShareCover('twitter')}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[#2A2520]/50 flex items-center"
                            >
                              <Twitter className="w-4 h-4 mr-2 text-[#1DA1F2]" />
                              Share on X
                            </button>
                            <button
                              onClick={() => handleShareCover('facebook')}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[#2A2520]/50 flex items-center"
                            >
                              <Facebook className="w-4 h-4 mr-2 text-[#4267B2]" />
                              Share on Facebook
                            </button>
                            <button
                              onClick={() => handleShareCover('copy')}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[#2A2520]/50 flex items-center"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Download Image
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <img 
                      src={`data:image/png;base64,${generatedCover}`} 
                      alt="Generated cover" 
                      className="max-h-64 mx-auto rounded-lg shadow-lg"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Color Theme Selection */}
            <div>
              <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-3">
                Design Style
              </label>
              
              {/* AI-Designed Option - Featured */}
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setSelectedTheme('ai')}
                  data-testid="theme-ai"
                  className={`w-full p-4 rounded-xl border-2 transition-all ${
                    selectedTheme === 'ai'
                      ? 'border-[#8B7A97] bg-[#F2EEF5] dark:bg-[#2B2530] shadow-sm'
                      : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#B9A9C4] dark:hover:border-[#6F5F7D]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-[#5C4D6B] flex items-center justify-center">
                        <Wand2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-[#2A2420] dark:text-[#F0EBE4]">AI-Designed Professional</div>
                        <div className="text-xs text-[#2A2420]/70 dark:text-[#F0EBE4]/70">
                          Genre-optimized typography, drop caps, elegant spacing
                        </div>
                      </div>
                    </div>
                    {selectedTheme === 'ai' && (
                      <span className="text-[#5C4D6B] dark:text-[#C9BBD8] text-sm font-medium">Selected</span>
                    )}
                  </div>
                  {formatGuide && selectedTheme === 'ai' && (
                    <div className="mt-3 pt-3 border-t border-[#D8CBB8] dark:border-[#3A3028] text-left">
                      <p className="text-xs text-[#6B5A7B] dark:text-[#C9BBD8]">
                        {formatGuide.designNotes || 'Professional design ready'}
                      </p>
                    </div>
                  )}
                </button>
                
                {selectedTheme === 'ai' && !formatGuide && (
                  <button
                    onClick={handleGenerateFormatGuide}
                    disabled={generatingFormat}
                    className="mt-2 w-full py-2 text-sm text-[#5C4D6B] dark:text-[#C9BBD8] hover:bg-[#F2EEF5] dark:hover:bg-[#2B2530] rounded-lg transition-colors flex items-center justify-center"
                  >
                    {generatingFormat ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing your book...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Preview AI design recommendations
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Classic Themes */}
              <div className="text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60 mb-2">Or choose a classic theme:</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(BOOK_THEMES).filter(([key]) => key !== 'ai').map(([key, themeData]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedTheme(key)}
                    data-testid={`theme-${key}`}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedTheme === key
                        ? 'border-current shadow-md scale-105'
                        : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#D4C4A8] dark:hover:border-gray-500'
                    }`}
                    style={{
                      backgroundColor: selectedTheme === key ? themeData.background : 'transparent'
                    }}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <div 
                        className="w-6 h-6 rounded-full border-2"
                        style={{ 
                          backgroundColor: themeData.primary,
                          borderColor: themeData.accent
                        }}
                      />
                      <div 
                        className="w-6 h-6 rounded-full border-2"
                        style={{ 
                          backgroundColor: themeData.secondary,
                          borderColor: themeData.accent
                        }}
                      />
                    </div>
                    <div className="text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4]">
                      {themeData.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Cover Preview */}
            <div className="bg-[#F8F5F0] dark:bg-[#2A2520]/50 rounded-xl p-4">
              <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                Cover Preview
              </h4>
              <div 
                className="rounded-lg overflow-hidden border-2 max-w-[200px] mx-auto aspect-[2/3] flex flex-col justify-end relative"
                style={{
                  backgroundColor: theme.background,
                  borderColor: theme.primary
                }}
              >
                {/* Cover Image (if any) */}
                {coverType === 'ai' && generatedCover && (
                  <img 
                    src={`data:image/png;base64,${generatedCover}`}
                    alt="Cover"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {coverType === 'image' && uploadedImagePreview && (
                  <img 
                    src={uploadedImagePreview}
                    alt="Cover"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {coverType === 'image' && !uploadedImagePreview && coverImageUrl && (
                  <img 
                    src={coverImageUrl}
                    alt="Cover"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                
                {/* Text overlay */}
                <div 
                  className="relative z-10 p-4 text-center"
                  style={{
                    backgroundColor: (coverType !== 'classic' && (generatedCover || uploadedImagePreview || coverImageUrl)) 
                      ? 'rgba(0,0,0,0.6)' 
                      : 'transparent'
                  }}
                >
                  <h3 
                    className="text-lg font-bold mb-1 leading-tight"
                    style={{ color: (coverType !== 'classic' && (generatedCover || uploadedImagePreview || coverImageUrl)) ? 'white' : theme.primary }}
                  >
                    {book.title}
                  </h3>
                  <p 
                    className="text-xs mb-2"
                    style={{ color: (coverType !== 'classic' && (generatedCover || uploadedImagePreview || coverImageUrl)) ? 'rgba(255,255,255,0.8)' : theme.secondary }}
                  >
                    {book.genre.toUpperCase()}
                  </p>
                  <p 
                    className="text-sm"
                    style={{ color: (coverType !== 'classic' && (generatedCover || uploadedImagePreview || coverImageUrl)) ? 'white' : theme.primary }}
                  >
                    By {authorName || 'Author'}
                  </p>
                </div>
              </div>
            </div>

            {/* Export Format Buttons */}
            <div>
              <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-3">
                Choose Export Format
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting || !authorName.trim()}
                  data-testid="export-pdf-button"
                  className="min-h-[76px] w-full rounded-xl border border-[#8F3D36]/25 bg-[#8E332E] px-3 py-3 text-white transition-colors hover:bg-[#7A2C28] disabled:pointer-events-none disabled:opacity-45 flex flex-col items-center justify-center gap-1 text-center"
                >
                  <FileText className="h-4 w-4 opacity-90" />
                  <span className="text-sm font-semibold leading-none">PDF</span>
                  <span className="text-[11px] leading-none text-white/72">{coverType === 'ai' && !generatedCover ? 'No cover' : 'With cover'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleExport('word')}
                  disabled={exporting || !authorName.trim()}
                  data-testid="export-word-button"
                  className="min-h-[76px] w-full rounded-xl border border-[#355778]/25 bg-[#314F6E] px-3 py-3 text-white transition-colors hover:bg-[#29445F] disabled:pointer-events-none disabled:opacity-45 flex flex-col items-center justify-center gap-1 text-center"
                >
                  <FileText className="h-4 w-4 opacity-90" />
                  <span className="text-sm font-semibold leading-none">Word</span>
                  <span className="text-[11px] leading-none text-white/72">Editable</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleExport('epub')}
                  disabled={exporting || !authorName.trim()}
                  data-testid="export-epub-button"
                  className="min-h-[76px] w-full rounded-xl border border-[#6F5F7D]/25 bg-[#5B4A6A] px-3 py-3 text-white transition-colors hover:bg-[#4D3F5A] disabled:pointer-events-none disabled:opacity-45 flex flex-col items-center justify-center gap-1 text-center"
                >
                  <BookOpen className="h-4 w-4 opacity-90" />
                  <span className="text-sm font-semibold leading-none">ePub</span>
                  <span className="text-[11px] leading-none text-white/72">E-readers</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleExport('email')}
                  disabled={exporting || !authorName.trim()}
                  data-testid="export-email-button"
                  className="min-h-[76px] w-full rounded-xl border border-[#3D7657]/25 bg-[#356B50] px-3 py-3 text-white transition-colors hover:bg-[#2D5C45] disabled:pointer-events-none disabled:opacity-45 flex flex-col items-center justify-center gap-1 text-center"
                >
                  <Mail className="h-4 w-4 opacity-90" />
                  <span className="text-sm font-semibold leading-none">Email</span>
                  <span className="text-[11px] leading-none text-white/72">Share</span>
                </button>
              </div>
              
              {coverType === 'ai' && !generatedCover && (
                <p className="text-sm text-stone-700 dark:text-stone-500 mt-2 text-center">
                  Generate a cover above to include it in the PDF
                </p>
              )}

              {/* Email Form */}
              {showEmailForm && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Book via Email
                  </h4>
                  <div className="flex space-x-2">
                    <Input
                      type="email"
                      value={emailRecipient}
                      onChange={(e) => setEmailRecipient(e.target.value)}
                      placeholder="recipient@email.com"
                      className="flex-1"
                      data-testid="email-recipient-input"
                    />
                    <Button
                      onClick={handleEmailExport}
                      disabled={exporting || !emailRecipient.trim()}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowEmailForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    Opens your email client with book content pre-filled
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Publishing Assistant Tab */}
        {activeTab === 'suggestions' && (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Generate Button */}
            {!exportSuggestions && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-[#F2EEF5] dark:bg-[#2B2530] rounded-full flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-[#6B5A7B] dark:text-[#C9BBD8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                  Publishing Assistant
                </h3>
                <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-6 max-w-md mx-auto">
                  Build a clear publishing package: positioning, export readiness, book description, back cover copy, keywords, and a cover brief.
                </p>
                <Button
                  onClick={handleGenerateExportSuggestions}
                  disabled={generatingSuggestions}
                  className="bg-[#5C4D6B] hover:bg-[#4A3D58] text-white"
                  data-testid="generate-suggestions-btn"
                >
                  {generatingSuggestions ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Reading Your Book...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Build Publishing Package
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Suggestions Content */}
            {exportSuggestions && (
              <div className="space-y-6">
                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleGenerateExportSuggestions}
                    disabled={generatingSuggestions}
                    variant="outline"
                    size="sm"
                    className="text-[#5C4D6B] border-[#CDBED8] hover:bg-[#F2EEF5]"
                  >
                    {generatingSuggestions ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    Regenerate
                  </Button>
                </div>

                {/* Export Readiness Score */}
                {exportSuggestions.export_ready_score && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-green-800 dark:text-green-200">Export Readiness</span>
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400">{exportSuggestions.export_ready_score}%</span>
                    </div>
                    <div className="mt-2 bg-green-200 dark:bg-green-800 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${exportSuggestions.export_ready_score}%` }}
                      />
                    </div>
                  </div>
                )}

                {exportSuggestions.positioning && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <BookMarked className="w-5 h-5 mr-2 text-[#6B5A7B]" />
                      Book Positioning
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      {[
                        ['What it is', exportSuggestions.positioning.what_it_is],
                        ['Audience', exportSuggestions.positioning.audience],
                        ['Reader promise', exportSuggestions.positioning.reader_promise],
                        ['Value', exportSuggestions.positioning.value],
                      ].filter(([, value]) => value).map(([label, value]) => (
                        <div key={label} className="p-3 bg-[#F8F5F0] dark:bg-[#2A2520] rounded-lg">
                          <p className="text-xs font-medium text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mb-1">{label}</p>
                          <p className="text-[#2A2420] dark:text-[#F0EBE4]">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {exportSuggestions.readiness_check && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-600" />
                      Publishing Checklist
                    </h4>
                    {exportSuggestions.readiness_check.needs_attention?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-2">Needs attention</p>
                        <ul className="space-y-2">
                          {exportSuggestions.readiness_check.needs_attention.map((item, idx) => (
                            <li key={idx} className="text-sm text-[#2A2420] dark:text-[#F0EBE4] flex gap-2">
                              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {exportSuggestions.readiness_check.optional_improvements?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-[#2A2420]/55 dark:text-[#F0EBE4]/55 mb-2">Optional improvements</p>
                        <ul className="space-y-2">
                          {exportSuggestions.readiness_check.optional_improvements.map((item, idx) => (
                            <li key={idx} className="text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80 flex gap-2">
                              <Circle className="w-3 h-3 text-[#8B7355] flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {exportSuggestions.readiness_check.next_best_action && (
                      <div className="p-3 bg-[#F4F0EC] dark:bg-[#2A2520]/70 rounded-lg text-sm text-[#2A2420] dark:text-[#F0EBE4]">
                        <span className="font-medium">Next best action: </span>
                        {exportSuggestions.readiness_check.next_best_action}
                      </div>
                    )}
                  </div>
                )}

                {(exportSuggestions.marketing_description || exportSuggestions.author_bio) && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {exportSuggestions.marketing_description && (
                      <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                        <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3">Book Description</h4>
                        <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4] leading-relaxed">{exportSuggestions.marketing_description}</p>
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(exportSuggestions.marketing_description);
                            toast.success('Description copied');
                          }}
                          variant="outline"
                          size="sm"
                          className="mt-3"
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                    )}
                    {exportSuggestions.author_bio && (
                      <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                        <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3">Author Bio</h4>
                        <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4] leading-relaxed">{exportSuggestions.author_bio}</p>
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(exportSuggestions.author_bio);
                            toast.success('Author bio copied');
                          }}
                          variant="outline"
                          size="sm"
                          className="mt-3"
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {(exportSuggestions.keywords?.length > 0 || exportSuggestions.categories?.length > 0) && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3">Keywords and Category Ideas</h4>
                    {exportSuggestions.keywords?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-[#2A2420]/55 dark:text-[#F0EBE4]/55 mb-2">Possible keywords</p>
                        <div className="flex flex-wrap gap-2">
                          {exportSuggestions.keywords.map((keyword, idx) => (
                            <span key={idx} className="px-2 py-1 rounded-full bg-[#F4F0EC] dark:bg-[#2A2520] text-xs text-[#2A2420] dark:text-[#F0EBE4]">{keyword}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {exportSuggestions.categories?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#2A2420]/55 dark:text-[#F0EBE4]/55 mb-2">Category directions</p>
                        <ul className="space-y-1">
                          {exportSuggestions.categories.map((category, idx) => (
                            <li key={idx} className="text-sm text-[#2A2420] dark:text-[#F0EBE4]">{category}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Book Title Alternatives */}
                {exportSuggestions.book_title_alternatives?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-[#6B5A7B]" />
                      Alternative Book Titles
                    </h4>
                    <div className="space-y-3">
                      {exportSuggestions.book_title_alternatives.map((alt, idx) => (
                        <div key={idx} className="p-3 bg-[#F8F5F0] dark:bg-[#2A2520] rounded-lg">
                          <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">"{alt.title}"</p>
                          <p className="text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60 mt-1">{alt.why}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subtitle Suggestion */}
                {exportSuggestions.subtitle_suggestion && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-2 flex items-center">
                      <Type className="w-5 h-5 mr-2 text-blue-500" />
                      Subtitle Suggestion
                    </h4>
                    <p className="text-lg font-medium text-blue-700 dark:text-blue-300 mb-1">
                      "{exportSuggestions.subtitle_suggestion.subtitle}"
                    </p>
                    <p className="text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                      {exportSuggestions.subtitle_suggestion.works_with}
                    </p>
                  </div>
                )}

                {/* Chapter Title Improvements */}
                {exportSuggestions.chapter_improvements?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-amber-500" />
                      Suggested Chapter Title Updates
                    </h4>
                    <div className="space-y-3">
                      {exportSuggestions.chapter_improvements.map((ch, idx) => (
                        <div key={idx} className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-xs text-amber-700 dark:text-amber-300">Chapter {ch.chapter_number}</p>
                              <p className="text-sm text-[#2A2420]/60 dark:text-[#F0EBE4]/60 line-through">{ch.current_title}</p>
                              <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">→ "{ch.suggested_title}"</p>
                            </div>
                          </div>
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{ch.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back Cover Blurb */}
                {exportSuggestions.back_cover_blurb && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-indigo-500" />
                      Back Cover Blurb
                    </h4>
                    <div className="p-4 bg-[#F4F0EC] dark:bg-[#2A2520]/70 rounded-lg border border-[#D8CBB8] dark:border-[#3A3028]">
                      <p className="text-[#2A2420] dark:text-[#F0EBE4] italic leading-relaxed">
                        {exportSuggestions.back_cover_blurb}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(exportSuggestions.back_cover_blurb);
                        toast.success('Blurb copied to clipboard!');
                      }}
                      variant="outline"
                      size="sm"
                      className="mt-3"
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Copy Blurb
                    </Button>
                  </div>
                )}

                {/* Cover Image Prompt */}
                {exportSuggestions.cover_image_prompt && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 flex items-center">
                      <Image className="w-5 h-5 mr-2 text-[#8A5E62]" />
                      AI Cover Concept
                    </h4>
                    <div className="p-4 bg-[#F6EEEC] dark:bg-[#2A2520]/70 rounded-lg border border-[#E1CFCB] dark:border-[#3A3028] mb-3">
                      <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                        {exportSuggestions.cover_image_prompt.prompt}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="px-2 py-1 bg-white dark:bg-[#22201C] rounded text-xs">
                          Mood: {exportSuggestions.cover_image_prompt.mood}
                        </span>
                        <span className="px-2 py-1 bg-white dark:bg-[#22201C] rounded text-xs">
                          Style: {exportSuggestions.cover_image_prompt.style}
                        </span>
                      </div>
                      {exportSuggestions.cover_image_prompt.color_palette && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">Colors:</span>
                          {exportSuggestions.cover_image_prompt.color_palette.map((color, i) => (
                            <div 
                              key={i}
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        handleUseSuggestedCoverPrompt();
                        setActiveTab('export');
                      }}
                      className="bg-[#8A5E62] hover:bg-[#744E52] text-white"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Use This for Cover Generation
                    </Button>
                  </div>
                )}

                {/* Recommendations */}
                {exportSuggestions.recommendations?.length > 0 && (
                  <div className="bg-[#F4F0EC] dark:bg-[#2A2520]/70 border border-[#D8CBB8] dark:border-[#3A3028] rounded-xl p-4">
                    <h4 className="font-semibold text-[#3D3530] dark:text-[#F0EBE4] mb-2">
                      💡 Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {exportSuggestions.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-sm text-[#6F675D] dark:text-[#CFC3B7] flex items-start">
                          <span className="mr-2">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Review Tab */}
        {activeTab === 'review' && (
          <div className="p-4 sm:p-6 space-y-6">
            {!review && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-full flex items-center justify-center">
                  <BookMarked className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                  Editorial Review
                </h3>
                <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-6 max-w-md mx-auto">
                  Get a warm editorial read of your book before you export — transitions, thin spots, unresolved threads, and an overall sense of where it stands.
                </p>
                <Button
                  onClick={handleRunReview}
                  disabled={reviewLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="run-review-btn"
                >
                  {reviewLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Reading your book...
                    </>
                  ) : (
                    <>
                      <BookMarked className="w-5 h-5 mr-2" />
                      Run Editorial Review
                    </>
                  )}
                </Button>
              </div>
            )}

            {review && (
              <div className="space-y-5">
                {/* Re-run button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleRunReview}
                    disabled={reviewLoading}
                    variant="outline"
                    size="sm"
                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  >
                    {reviewLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Re-run
                  </Button>
                </div>

                {/* Readiness banner */}
                <div className={`rounded-xl p-4 flex items-start gap-3 ${
                  review.overall_readiness === 'ready'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                    : review.overall_readiness === 'needs_attention'
                    ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                    : 'bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700'
                }`}>
                  {review.overall_readiness === 'ready'
                    ? <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    : review.overall_readiness === 'needs_attention'
                    ? <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    : <Circle className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" />
                  }
                  <div>
                    <p className={`font-semibold text-sm mb-1 ${
                      review.overall_readiness === 'ready' ? 'text-emerald-800 dark:text-emerald-200'
                      : review.overall_readiness === 'needs_attention' ? 'text-amber-800 dark:text-amber-200'
                      : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {review.overall_readiness === 'ready' ? 'Ready to export'
                        : review.overall_readiness === 'needs_attention' ? 'Almost there'
                        : 'Still in progress'}
                    </p>
                    <p className="text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80 leading-relaxed">
                      {review.readiness_note}
                    </p>
                  </div>
                </div>

                {/* Thin chapters */}
                {review.thin_chapters?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 text-sm">
                      Chapters that might have more to say
                    </h4>
                    <div className="space-y-3">
                      {review.thin_chapters.map((item, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-3 p-3 bg-[#F8F5F0] dark:bg-[#2A2520] rounded-lg">
                          <div>
                            <p className="text-xs text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mb-1">Chapter {item.chapter_number}</p>
                            <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4]">{item.note}</p>
                          </div>
                          {chapters.find(c => c.chapter_number === item.chapter_number) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGoToChapter(item.chapter_number)}
                              className="flex-shrink-0 text-xs border-[#8B7355] text-[#7C766E] hover:bg-[#8B7355]/10"
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Weak transitions */}
                {review.weak_transitions?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 text-sm">
                      Transitions worth smoothing
                    </h4>
                    <div className="space-y-3">
                      {review.weak_transitions.map((item, idx) => (
                        <div key={idx} className="p-3 bg-[#F8F5F0] dark:bg-[#2A2520] rounded-lg">
                          <p className="text-xs font-medium text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mb-1">{item.between}</p>
                          <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4]">{item.note}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tone breaks */}
                {review.tone_breaks?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 text-sm">
                      Voice or tone shifts to consider
                    </h4>
                    <div className="space-y-3">
                      {review.tone_breaks.map((item, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-3 p-3 bg-[#F8F5F0] dark:bg-[#2A2520] rounded-lg">
                          <div>
                            <p className="text-xs text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mb-1">Chapter {item.chapter_number}</p>
                            <p className="text-sm text-[#2A2420] dark:text-[#F0EBE4]">{item.note}</p>
                          </div>
                          {chapters.find(c => c.chapter_number === item.chapter_number) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGoToChapter(item.chapter_number)}
                              className="flex-shrink-0 text-xs border-[#8B7355] text-[#7C766E] hover:bg-[#8B7355]/10"
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unresolved threads */}
                {review.unresolved_threads?.length > 0 && (
                  <div className="bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl p-4">
                    <h4 className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-3 text-sm">
                      Story threads still in the air
                    </h4>
                    <ul className="space-y-2">
                      {review.unresolved_threads.map((thread, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80">
                          <span className="text-amber-500 mt-0.5">•</span>
                          {thread}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* All clear message */}
                {review.overall_readiness === 'ready' &&
                  !review.thin_chapters?.length &&
                  !review.weak_transitions?.length &&
                  !review.tone_breaks?.length &&
                  !review.unresolved_threads?.length && (
                  <div className="text-center py-4 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                    Nothing flagged — your book reads beautifully from start to finish.
                  </div>
                )}

                {/* Export CTA */}
                <div className="pt-2 border-t border-[#E0D8CC] dark:border-[#2A2520]/50">
                  <Button
                    onClick={() => setActiveTab('export')}
                    className="w-full bg-[#2A2420] hover:bg-[#1A1A1A] dark:bg-[#F8F5F0] dark:hover:bg-white dark:text-[#2A2420] text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Go to Export
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="p-6">
            {loadingExports ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5C4D6B] dark:border-[#F5F1E8] mx-auto"></div>
                <p className="mt-4 text-[#2A2420]/70 dark:text-[#F0EBE4]/70">Loading exports...</p>
              </div>
            ) : exports.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#E8E0D4] dark:bg-[#2A2520] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="w-8 h-8 text-[#2A2420] dark:text-[#F0EBE4]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2A2420] dark:text-[#F0EBE4] mb-2">No exports yet</h3>
                <p className="text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-4">
                  Export your book to see it here
                </p>
                <Button onClick={() => setActiveTab('export')} variant="outline">
                  Create Export
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70 mb-4">
                  Export history — re-export any format to download again.
                </p>
                {exports.map((exp) => (
                  <div
                    key={exp.export_id}
                    className="flex items-center justify-between p-4 bg-[#F8F5F0] dark:bg-[#2A2520]/50 rounded-xl"
                    data-testid={`export-item-${exp.export_id}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-3xl">{getFormatIcon(exp.format)}</div>
                      <div>
                        <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">
                          {exp.file_name}
                        </p>
                        <div className="flex items-center space-x-3 text-xs text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(exp.created_at)}
                          </span>
                          <span>•</span>
                          <span>{exp.format.toUpperCase()}</span>
                          <span>•</span>
                          <span>{formatFileSize(exp.file_size)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {exp.format !== 'email' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab('export')}
                          data-testid={`reexport-${exp.export_id}`}
                          className="text-[#2A2420] dark:text-[#F0EBE4] border-[#E0D8CC] dark:border-[#2A2520] hover:bg-[#E8E0D4] dark:hover:bg-[#2A2520]/50"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Re-export
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteExport(exp.export_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {!exportSuccess && (
          <div className="sticky bottom-0 bg-white dark:bg-[#22201C] border-t border-[#E0D8CC] dark:border-[#2A2520]/50 px-6 py-4 flex justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              className="dark:border-[#2A2520] dark:text-[#F0EBE4] dark:hover:bg-[#2A2520]/50"
            >
              Close
            </Button>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default ExportBookModal;
